// @ts-nocheck
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box, Fab, Icon } from '@material-ui/core';
import {
  useAlert,
  useDialog,
  useHistoryState,
  useIfMounted,
  useOnMount,
} from '../../../../common/hooks';
import { AlertSeverity } from '../../../../common/providers';
import { LoadingWrapper, Panel } from '../../../../common/components';
import { AppRoute } from '../../../app';
import { selectCustomers, selectPartners } from '../../state/customerSelectors';
import { createUser, fetchPartners, searchCustomers } from '../../state/customerActions';
import CustomerSearchForm from './partials/CustomerSearchForm';
import CustomerSearchResults from './partials/CustomerSearchResults';
import UserForm from '../user/UserForm';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  results: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& > *:first-child': {
      marginTop: theme.spacing(3),
    },
  },
  dialog: {
    padding: '0',
    marginBottom: '0',
  },
  dialogContent: {
    display: 'flex',
    maxWidth: '925px',
    overflow: 'hidden',
  },
  fab: {
    position: 'absolute',
    top: '12px',
    right: '36px',
    zIndex: '100',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.23)',
    },
  },
}));

function CustomerSearchPage() {
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useHistoryState('searchParams', null);
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const { showAlert } = useAlert();
  const customers = useSelector(selectCustomers);
  const partners = useSelector(selectPartners);
  const history = useHistory();
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  useOnMount(() => {
    async function handleFetchData() {
      try {
        await dispatch(fetchPartners());
      } catch (e) {
        showAlert({
          title: t('customer.customerSearchFetchPartnersError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    }
    if (_.isEmpty(partners)) {
      handleFetchData();
    }
  });

  const handleSearch = useCallback(
    async formData => {
      try {
        setLoading(true);
        setSearchParams(formData);
        await dispatch(searchCustomers(formData));
      } catch (e) {
        showAlert({
          title: t('customer.customerSearchFetchCustomersError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    },
    [dispatch, ifMounted, setSearchParams, showAlert, t]
  );

  const handleFetchCustomers = useCallback(
    async page => {
      try {
        await dispatch(searchCustomers(searchParams, page));
      } catch (e) {
        showAlert({
          title: t('customer.customerSearchFetchCustomersError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, searchParams, showAlert, t]
  );

  const handleSelectCustomer = useCallback(
    params => {
      history.push(AppRoute.CustomerDetails.replace(':userId', params.row.id), {
        from: history.location,
      });
    },
    [history]
  );

  const handleCreateUser = useCallback(
    async userData => {
      try {
        const { userId } = await dispatch(createUser(userData));
        ifMounted(() =>
          history.push(AppRoute.CustomerDetails.replace(':userId', userId), {
            from: history.location,
          })
        );
        showAlert({
          title: t('common.success'),
          message: t('user.userCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('user.userCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, history, ifMounted, showAlert, t]
  );

  const handleShowCreateUser = () => {
    showDialog({
      title: t('user.userCreate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <UserForm
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            saveButtonText={t('user.userCreate')}
          />
        </Box>
      ),
      onConfirm: handleCreateUser,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  return (
    <Box id="CustomerSearch-container" className={classes.root}>
      <Fab
        className={classes.fab}
        color="primary"
        aria-label="createUser"
        onClick={handleShowCreateUser}
      >
        <Icon>person_add</Icon>
      </Fab>
      <Panel id="CustomerSearch-form" title={t('customer.customerSearchTitle')}>
        <CustomerSearchForm partners={_.defaultTo(partners, [])} onSearch={handleSearch} />
      </Panel>
      <Box className={classes.results}>
        <LoadingWrapper loading={loading}>
          {customers && searchParams && (
            <Panel id="CustomerSearch-results" variant="contained">
              <CustomerSearchResults
                data={customers}
                onSelectCustomer={handleSelectCustomer}
                onFetchData={handleFetchCustomers}
              />
            </Panel>
          )}
        </LoadingWrapper>
      </Box>
    </Box>
  );
}

export default CustomerSearchPage;
